import React, { useEffect, useState } from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  IconButton,
  Avatar,
  Chip,
  Stack,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  PersonOutline as PersonOutlineIcon,
  History as HistoryIcon,
  ManageAccounts as ManageAccountsIcon,
  Terminal as TerminalIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getUserInfo, setUserInfo } from '../utils/auth';
import { withMediaToken } from '../utils/media';
import { AuthService } from '../services/AuthService';
import usePermissions from '../hooks/usePermissions';
import ProfileDialog from './ProfileDialog';
import McpConnectDialog from './McpConnectDialog';
import NotificationBell from './NotificationBell';

/** role 코드 → 한국어 라벨 + 칩 색상 매핑. 모르면 grey로 fallback. */
const ROLE_META: Record<string, { label: string; color: 'primary' | 'success' | 'default' | 'warning' }> = {
  admin: { label: '관리자', color: 'warning' },
  editor: { label: '편집자', color: 'primary' },
  viewer: { label: '뷰어', color: 'default' },
  master: { label: 'Master', color: 'warning' },
};

/** 이름/이메일에서 2글자 이내 이니셜 추출. 한글이면 첫 글자 1개. */
export function initialsOf(name?: string, email?: string): string {
  const src = (name || email || '').trim();
  if (!src) return '?';
  // 한글 (가-힣) 이 포함되어 있으면 첫 글자만.
  if (/[가-힣]/.test(src)) return src.charAt(0);
  // 영문: 공백 단어 첫 글자 두 개.
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return src.charAt(0).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/** 이름/이메일 기반 결정적 색상 — 같은 사용자는 항상 같은 색. */
export function avatarBgColor(seed: string): string {
  const palette = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c62828', '#0097a7', '#5d4037'];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

/**
 * User Profile Menu Component
 * 우상단 아바타 → 클릭 시 메뉴 (이메일 / 이름 / 권한 / 프로필 / 변경 이력 / 로그아웃).
 * 아바타는 사용자 이미지가 없을 때 이니셜 fallback. role 은 메뉴 상단에 chip 으로 노출.
 */
const UserProfileMenu: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAdmin } = usePermissions();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [mcpOpen, setMcpOpen] = useState(false);
  // userInfo 는 localStorage 기반 — 모달에서 이름이 바뀌면 강제 리렌더 위해 키 변경.
  const [bump, setBump] = useState(0);
  const userInfo = getUserInfo();
  void bump; // 강제 리렌더 트리거 — getUserInfo() 가 새 값을 읽도록.

  // 페이지 새로고침 직후 한 번 서버에서 최신 프로필을 받아 localStorage 와
  // 헤더 아바타 src 를 동기화. 로그인 응답에는 profile_image_url 이 없기에
  // 별도로 한 번 fetch 가 필요하다. 실패는 silent — 헤더 표시는 이니셜 fallback.
  useEffect(() => {
    if (!userInfo || userInfo.profile_image_url) return;
    AuthService.getProfile()
      .then((resp) => {
        if (!resp.success || !resp.data) return;
        if (!resp.data.profile_image_url) return;
        const cur = getUserInfo();
        if (!cur) return;
        setUserInfo({ ...cur, profile_image_url: resp.data.profile_image_url });
        setBump((n) => n + 1);
      })
      .catch(() => { /* silent */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenProfile = () => {
    handleClose();
    setProfileOpen(true);
  };

  const handleLogout = async () => {
    handleClose();
    await AuthService.logout();
    navigate('/login');
  };

  const roleKey = (userInfo?.role || '').toLowerCase();
  const roleMeta = ROLE_META[roleKey] ?? { label: userInfo?.role || '권한 없음', color: 'default' as const };
  const initials = initialsOf(userInfo?.name, userInfo?.email);
  const avatarBg = avatarBgColor(userInfo?.email || userInfo?.name || 'unknown');
  // 백엔드가 채우는 안정 경로. 없으면 이니셜 fallback. ProfileDialog 가 업로드
  // 직후 setUserInfo 로 갱신하므로 bump 와 함께 자연스럽게 리렌더된다.
  // `<img>` 는 Authorization 헤더를 못 붙이므로 withMediaToken 으로 ?token= 을 실어 보낸다.
  const avatarSrc: string | undefined = userInfo?.profile_image_url
    ? withMediaToken(userInfo.profile_image_url)
    : undefined;

  return (
    <>
      <NotificationBell />
      <Tooltip title={userInfo?.name ? `${userInfo.name} (${roleMeta.label})` : '계정'} arrow>
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{
            p: 0.25,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.1),
            },
          }}
          aria-label="계정 메뉴 열기"
        >
          <Avatar
            src={avatarSrc}
            sx={{
              width: 32,
              height: 32,
              fontSize: '0.85rem',
              fontWeight: 600,
              bgcolor: avatarBg,
              color: '#fff',
            }}
          >
            {initials}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 248,
              borderRadius: 2,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.75 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              src={avatarSrc}
              sx={{
                width: 44,
                height: 44,
                fontSize: '1rem',
                fontWeight: 600,
                bgcolor: avatarBg,
                color: '#fff',
              }}
            >
              {initials}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body1" fontWeight={700} noWrap title={userInfo?.name}>
                {userInfo?.name || '이름 미설정'}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                title={userInfo?.email}
                sx={{ display: 'block', mt: 0.25 }}
              >
                {userInfo?.email}
              </Typography>
              <Box sx={{ mt: 0.75 }}>
                <Chip
                  label={roleMeta.label}
                  size="small"
                  color={roleMeta.color}
                  variant="filled"
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                />
              </Box>
            </Box>
          </Stack>
        </Box>
        <Divider />
        <MenuItem onClick={handleOpenProfile} sx={{ mt: 0.5 }}>
          <ListItemIcon>
            <PersonOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>프로필</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleClose(); navigate('/audit-logs'); }}>
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>변경 이력</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleClose(); setMcpOpen(true); }}>
          <ListItemIcon>
            <TerminalIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Claude Code 연결</ListItemText>
        </MenuItem>
        {isAdmin && (
          <MenuItem onClick={() => { handleClose(); navigate('/projects?tab=users'); }}>
            <ListItemIcon>
              <ManageAccountsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>유저 관리</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>로그아웃</ListItemText>
        </MenuItem>
      </Menu>

      <ProfileDialog
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        onUpdated={() => setBump((n) => n + 1)}
      />
      <McpConnectDialog open={mcpOpen} onClose={() => setMcpOpen(false)} />
    </>
  );
};

export default UserProfileMenu;
